import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SmartFormComponent } from 'ngx-smart-form';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  skipWhile,
  startWith,
  Subject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  IPratica,
  Pratica,
  PraticaProgress,
  StatoPratica,
  Target,
} from '../../../interfaces/pratica';
import { BeLiberoProfessionista } from '../../applicant/models/be-professionista';
import { IFactory } from '../../applicant/models/i-factory';
import { BEImpresa, Impresa } from '../../applicant/models/impresa';
import { LiberoProfessionista } from '../../applicant/models/libero-professionista';
import { PersonaGiuridica } from '../../applicant/models/persona-giuridica';
import { ProfessionistaFactory } from '../../applicant/models/professionista-factory';
import { PraticaSection } from '../../pratica/models/pratica-section';
import { StoreData } from '../models/store-data';
import { AuthService } from './auth.service';
import { PersonaGiuridicaService } from './persona-giuridica.service';
import { PraticaService } from './pratica.service';
import { TaskService } from './task.service';
import { ImpresaFactory } from '../../applicant/models/impresa-factory';

@Injectable({
  providedIn: 'root',
})
export class PraticaProgressService extends PraticaService {
  private readonly endpoint = environment.api;

  private _selectedPratica = new BehaviorSubject<StoreData<Pratica>>(
    new StoreData()
  );
  selectedPratica$ = this._selectedPratica.asObservable();
  validPratica$ = this._selectedPratica
    .asObservable()
    .pipe(filter((storedData) => storedData.state === 'completed'));

  progress$ = new BehaviorSubject<Record<PraticaSection, number>>({
    applicant: 0,
    requests: 0,
    anagraphics: 0,
    score: 0,
    'presentation-sheet': 0,
    documents: 0,
  });

  total = new BehaviorSubject<number>(0);

  triggerSave$ = new Subject<void>();
  private _sectionHasUpdated$ = new BehaviorSubject<number>(0);

  sectionHasUpdated$ = this._sectionHasUpdated$.asObservable();

  isReadyForSignature$: Observable<boolean>;

  isFrozen = false;

  isOutcome = false;

  hasJustBeenSigned = new Subject<boolean>();

  constructor(
    http: HttpClient,
    private router: Router,
    private auth: AuthService,
    private pgService: PersonaGiuridicaService
  ) {
    super(http);
    this.isReadyForSignature$ = combineLatest([
      this.total.pipe(
        map((total) => total === 100),
        distinctUntilChanged()
      ),
      this.validPratica$,
    ]).pipe(
      map(([isCompleted, praticaData]) => {
        if (
          isCompleted &&
          praticaData.data?.state === StatoPratica.APERTA &&
          this.auth.me?.id === this.selectedPratica?.user
        ) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  getPratica(id: string | undefined = this.selectedPratica!.id) {
    return this.getById(id).pipe(
      map((pratica) => {
        return new Pratica(pratica);
      }),
      tap((pratica) => {
        if (pratica) {
          this.performPraticaStateChecks(pratica);
          this.init(pratica.progress);
        } else {
          throw 'Pratica non trovata';
        }
        this._selectedPratica.next(
          this._selectedPratica.value.update('completed', pratica)
        );
      }),
      catchError((err) => {
        this.router.navigate(['home', 'requests']);
        console.error(err);
        return throwError(() => 'Pratica non trovata');
      })
    );
  }

  private init(progress: Partial<PraticaProgress> | undefined) {
    let p: Record<PraticaSection, number> = {
      applicant: progress?.applicant || 0,
      requests: progress?.requests || 0,
      anagraphics: progress?.anagraphics || 0,
      score: progress?.score || 0,
      'presentation-sheet': progress?.presentationSheet || 0,
      documents: progress?.documents || 0,
    };

    this.progress$.next(p);

    //this.total.next(progress?.total || 0);
  }

  aggregateProgress(
    componentList: SmartFormComponent[],
    section: PraticaSection
  ) {
    return this.trackFormChanges(componentList).pipe(
      tap((progress) => {
        this.updateProgress(section, progress);
      })
    );
  }

  trackFormChanges<T = any>(componentList: SmartFormComponent<T>[]) {
    const progressList$ = componentList.map((c) =>
      c.form.valueChanges.pipe(startWith(undefined))
    );
    return combineLatest(progressList$).pipe(
      map((_) => this.calcTotFields(componentList)),
      skipWhile((totFields) => totFields === 0),
      map((totFields) => {
        const totValidFields = this.calcTotValidFields(componentList);
        const formCompletionPercentage = this.calcFormCompletionPercentage(
          totFields,
          totValidFields
        );
        return formCompletionPercentage;
      })
    );
  }

  private calcTotFields<T = any>(componentList: SmartFormComponent<T>[]) {
    const totFields = componentList
      .map((c) => c.fields.flat().length)
      .reduce((p, c) => p + c);
    return totFields;
  }

  private calcTotValidFields<T = any>(componentList: SmartFormComponent<T>[]) {
    const totValidFields = componentList
      .map((c) => {
        let ctrls = Object.values(c.form.controls);
        let validCtrls = ctrls.filter((ctrl) => !ctrl.invalid && !ctrl.pending);
        let validFieldsCount = validCtrls.length;
        return validFieldsCount;
      })
      .reduce((p, c) => p + c);
    return totValidFields;
  }

  private calcFormCompletionPercentage<T = any>(
    totFields: number,
    totValidFields: number
  ) {
    let ratio = totFields ? (totValidFields * 100) / totFields : 0;
    let rounded = Math.floor(ratio);
    return rounded;
  }

  updateProgress(section: PraticaSection, progress: number) {
    let p: Record<PraticaSection, number> = {
      ...this.progress,
      [section]: progress,
    };
    this.progress$.next(p);
  }

  saveProgress() {
    this.triggerSave$.next();
  }

  notifySectionUpdate() {
    this._sectionHasUpdated$.next(this._sectionHasUpdated$.value + 1);
  }

  calcTotal() {
    let progressList = Object.values(this.progress);
    let sum = progressList.reduce((p, c) => p + c);
    let media = sum / progressList.length;
    let rounded = Math.floor(media);
    this.total.next(rounded);
  }

  updatePraticaProgress() {
    let body: Partial<IPratica> = {
      progress: {
        ...this.progress,
        presentationSheet: this.progress['presentation-sheet'],
        total: this.total.value,
      },
    };
    if (this.pgService.cachedPersonaGiuridica) {
      body.denominazione = this.pgService.cachedPersonaGiuridica.denominazione;
    }
    return this.edit(this.selectedPratica!.id, body);
  }

  get scoreDisabled() {
    return (
      this.selectedPratica?.type === 'AML' &&
      (this.progress.applicant < 100 ||
        this.progress.anagraphics < 100 ||
        this.progress.requests < 100) &&
      this.progress.score < 100
    );
  }

  get presentationSheetDisabled() {
    return this.selectedPratica?.type === 'AML' && this.progress.score < 100;
  }

  get isFrozenOrConfermata() {
    return (
      this.isFrozen || this.selectedPratica?.state === StatoPratica.COMPLETATA
    );
  }

  get selectedPratica() {
    return Object.freeze(this._selectedPratica.value.data);
  }

  get canEditDataOnSignedPratica() {
    /* return (
      this.auth.isCdc &&
      (this.selectedPratica?.state === StatoPratica.FIRMATA ||
        this.selectedPratica?.state === StatoPratica.IN_ASSEGNAZIONE)
    ); */
    return false;
  }

  resetPratica() {
    this._selectedPratica.next(this._selectedPratica.value.update('init'));
    this._sectionHasUpdated$.next(0);
    this.hasJustBeenSigned.next(false);
  }

  get progress() {
    return this.progress$.value;
  }

  calcComplexCompletionPercentage(
    componentCompletionPercentage: number,
    percentageWeight: number
  ) {
    let completionPercentage = componentCompletionPercentage * percentageWeight;
    let roundedPercentage = Math.round(completionPercentage);
    return roundedPercentage;
  }

  private performPraticaStateChecks(pratica: Pratica) {
    let editableStates = [StatoPratica.APERTA];
    let praticaIsLocked = !editableStates.includes(pratica.state);
    let praticaIsAssignedToUser = pratica?.user === this.auth.me?.id;
    let isReadOnlyUser: boolean;
    isReadOnlyUser = !praticaIsAssignedToUser;
    let initialStates = [StatoPratica.APERTA];

    this.isFrozen = praticaIsLocked || isReadOnlyUser;

    this.isOutcome = false;
  }
}
